"use client";
import Link from "next/link";
import { useRef, useState, useEffect, type FormEvent } from "react";
import { commerceRequest } from "./CheckoutForm";
import { PrivateApplication } from "./PurchaseJourney";

export function ApplyPage() {
  const [invited, setInvited] = useState(false);
  useEffect(() => {
    const update = () => {
      const hash = window.location.hash.slice(1);
      // A skip-to-content anchor must not replace an open buyer invitation.
      if (hash !== "main") setInvited(Boolean(hash && hash !== "application"));
    };
    update();
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, []);
  return invited ? (
    <main id="main" className="co-form-page">
      <PrivateApplication />
    </main>
  ) : (
    <PublicApplication />
  );
}

function PublicApplication() {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const confirmation = useRef<HTMLDivElement>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const data = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await commerceRequest("prelaunch-apply", {
        ...data,
        contactConsent: data.contactConsent === "on",
      });
      setSent(true);
      requestAnimationFrame(() => confirmation.current?.focus());
    } catch {
      setError(
        "We couldn’t save your application. Please try again shortly, or contact jerrod.a@haulbase.ai.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main id="main" className="co-apply">
      <section className="co-section co-apply-hero">
        <div>
          <p className="co-eyebrow">
            <span className="co-prelaunch-dot" /> HAULBASE / PRE-LAUNCH
            APPLICATIONS
          </p>
          <h1>
            The next way
            <br />
            to run freight.
            <br />
            <em>Built with you.</em>
          </h1>
          <p className="co-apply-lead">
            You know where the work gets stuck. We’re building the execution
            layer to help move it forward.
          </p>
          <a href="#application" className="co-button">
            Apply for early access <span aria-hidden="true">↗</span>
          </a>
          <p className="co-fine">
            By application. Reviewed personally. No purchase required.
          </p>
        </div>
        <aside
          className="co-apply-blueprint"
          aria-label="The Haulbase direction"
        >
          <div className="co-blueprint-top">
            <span>THE OPERATING MODEL</span>
            <span>01 → 02</span>
          </div>
          <div className="co-blueprint-step">
            <span className="co-step-number">01</span>
            <div>
              <p className="co-eyebrow">START WITH THE IDEAS</p>
              <h2>A better operating rhythm.</h2>
              <p>
                The $37 playbook: SOPs, communication templates and practical
                tools your team can put to work.
              </p>
              <Link href="/playbook">
                Explore the playbook <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
          <div className="co-blueprint-connector" aria-hidden="true">
            ↓
          </div>
          <div className="co-blueprint-step">
            <span className="co-step-number">02</span>
            <div>
              <p className="co-eyebrow">NEXT / THE EXECUTION LAYER</p>
              <h2>
                From knowing.
                <br />
                <em>To doing.</em>
              </h2>
              <p>
                Haulbase is in development. Our direction: turn freight
                workflows into clear next actions, follow-through and
                visibility.
              </p>
              <span className="co-prelaunch-label">
                IN DEVELOPMENT · ACCESS BY INVITATION
              </span>
            </div>
          </div>
          <p className="co-blueprint-note">
            Product direction, not a live app preview. Timing and features are
            still taking shape.
          </p>
        </aside>
      </section>
      <section className="co-section co-apply-details">
        <p className="co-eyebrow">A FOCUSED FIRST CHAPTER</p>
        <div className="co-apply-principles">
          <article>
            <span>01 / THE PEOPLE</span>
            <h3>Close to the work.</h3>
            <p>
              Freight operators, brokerage teams and leaders who can point to a
              real problem in their day.
            </p>
          </article>
          <article>
            <span>02 / THE FIT</span>
            <h3>Specific beats broad.</h3>
            <p>
              Tell us about the handoff, follow-up or exception that costs your
              team the most attention.
            </p>
          </article>
          <article>
            <span>03 / THE APPROACH</span>
            <h3>Build with intention.</h3>
            <p>
              We’ll review for workflow fit and contact selected applicants as
              we’re ready for conversations and early access.
            </p>
          </article>
        </div>
      </section>
      <section
        id="application"
        className="co-section co-apply-intake"
        aria-labelledby="application-title"
      >
        <div className="co-apply-intro">
          <p className="co-eyebrow">YOUR OPERATION / OUR NEXT CONVERSATION</p>
          <h2 id="application-title">
            Show us where
            <br />
            <em>the work slows down.</em>
          </h2>
          <p>
            A few details about your team and the problem you want to solve. We
            read applications for fit, not company size.
          </p>
          <dl>
            <div>
              <dt>What happens next?</dt>
              <dd>
                We review your application and may contact you about your
                workflow and future access.
              </dd>
            </div>
            <div>
              <dt>Does applying cost anything?</dt>
              <dd>
                No. The $37 playbook is a separate digital product. Buying it
                does not guarantee app access.
              </dd>
            </div>
            <div>
              <dt>When can I use the app?</dt>
              <dd>
                Haulbase is pre-launch. Applying does not create an account,
                reserve a launch date or activate a workspace.
              </dd>
            </div>
          </dl>
          <p className="co-fine">
            Already received a buyer invitation? Use the personal link in your
            email.
          </p>
        </div>
        <div className="co-apply-form-panel">
          {sent ? (
            <div
              className="co-application-received"
              role="status"
              tabIndex={-1}
              ref={confirmation}
            >
              <span className="co-received-mark" aria-hidden="true">
                ✓
              </span>
              <p className="co-eyebrow">APPLICATION RECEIVED</p>
              <h2>
                You’re in
                <br />
                <em>the conversation.</em>
              </h2>
              <p>
                Thanks for sharing your operation. We’ll review your application
                and contact you if there’s a fit for the next stage.
              </p>
              <p>
                This is an application for future access. It does not activate
                an account or guarantee an invitation.
              </p>
              <Link className="co-button" href="/playbook">
                Explore the $37 playbook <span aria-hidden="true">↗</span>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} aria-busy={busy}>
              <div className="co-apply-form-heading">
                <h3>Your early-access application</h3>
                <p>All fields are required. About 3 minutes.</p>
              </div>
              <div className="co-form-grid">
                {(
                  [
                    ["name", "Your name", "Alex Morgan", "name"],
                    ["email", "Work email", "you@company.com", "email"],
                    ["company", "Company", "Your company", "organization"],
                    [
                      "role",
                      "Your role",
                      "Owner, operations lead, dispatcher…",
                      "organization-title",
                    ],
                    [
                      "teamSize",
                      "Operations team size",
                      "e.g. 5 people",
                      "off",
                    ],
                    ["volume", "Loads per month", "An estimate is fine", "off"],
                    [
                      "modes",
                      "Freight modes",
                      "e.g. FTL, LTL, intermodal",
                      "off",
                    ],
                    [
                      "currentTools",
                      "Current operating tools",
                      "TMS, spreadsheets, email…",
                      "off",
                    ],
                  ] as const
                ).map(([name, label, placeholder, autoComplete]) => (
                  <div key={name}>
                    <label htmlFor={`apply-${name}`}>{label}</label>
                    <input
                      id={`apply-${name}`}
                      name={name}
                      type={name === "email" ? "email" : "text"}
                      autoComplete={autoComplete}
                      placeholder={placeholder}
                      maxLength={name === "email" ? 254 : 200}
                      required
                    />
                  </div>
                ))}
              </div>
              <label htmlFor="apply-pain">Where does the work get stuck?</label>
              <textarea
                id="apply-pain"
                name="pain"
                placeholder="Describe one recurring problem, how you handle it today, and what a better outcome would look like."
                maxLength={1500}
                required
              />
              <label htmlFor="apply-readiness">
                When would you be ready for an early-access conversation?
              </label>
              <select
                id="apply-readiness"
                name="readiness"
                defaultValue=""
                required
              >
                <option value="" disabled>
                  Select your timing
                </option>
                <option>Now</option>
                <option>Within 90 days</option>
                <option>Exploring</option>
              </select>
              <div className="co-form-trap" aria-hidden="true">
                <label htmlFor="apply-website">Leave this empty</label>
                <input
                  id="apply-website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <label className="co-apply-consent">
                <input type="checkbox" name="contactConsent" required />
                <span>
                  I agree to be contacted about my application and early access.
                  I’ve read the{" "}
                  <Link href="/legal/privacy">privacy policy</Link>.
                </span>
              </label>
              {error && (
                <p className="co-error" role="alert">
                  {error}
                </p>
              )}
              <button className="co-button" disabled={busy}>
                {busy ? "Submitting…" : "Submit application"}
                <span aria-hidden="true">↗</span>
              </button>
              <p className="co-fine">
                No payment. No automatic enrollment. Just a considered next
                step.
              </p>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
